/**
 * FormBuilderContext - Shared state management for form builder
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { z } from 'zod';
import {
  FormSchema,
  FormState,
  FormBuilderContextValue,
  createFieldValidationSchema
} from '../types/forms';

const FormBuilderContext = createContext<FormBuilderContextValue | undefined>(undefined);

export interface FormBuilderProviderProps {
  children: React.ReactNode;
  template: FormSchema | null;
  initialData?: Record<string, any>;
  language?: string;
  mode?: 'create' | 'edit' | 'view' | 'preview';
  onSubmit?: (data: Record<string, any>) => void | Promise<void>;
  onChange?: (data: Record<string, any>) => void;
}

export const FormBuilderProvider: React.FC<FormBuilderProviderProps> = ({
  children,
  template,
  initialData = {},
  language = 'en',
  mode = 'create',
  onSubmit,
  onChange,
}) => {
  // Form state
  const [formState, setFormState] = useState<FormState>({
    data: initialData,
    errors: {},
    touched: {},
    isDirty: false,
    isSubmitting: false,
    isValid: true,
  });

  const [currentLanguage, setCurrentLanguage] = useState(language);
  const [currentMode, setCurrentMode] = useState(mode);
  const [translations] = useState<Record<string, string>>({});

  // Update form data
  const updateField = useCallback((name: string, value: any) => {
    setFormState((prev) => {
      const newData = { ...prev.data, [name]: value };
      const newState = {
        ...prev,
        data: newData,
        isDirty: true,
      };

      // Clear error when field is updated
      if (prev.errors[name]) {
        newState.errors = { ...prev.errors };
        delete newState.errors[name];
      }

      // Notify onChange
      onChange?.(newData);

      return newState;
    });
  }, [onChange]);

  // Touch a field
  const touchField = useCallback((name: string) => {
    setFormState((prev) => ({
      ...prev,
      touched: { ...prev.touched, [name]: true },
    }));
  }, []);

  // Validate a single field
  const validateField = useCallback((name: string): string | undefined => {
    if (!template) return undefined;

    // Find the field in the template
    const field = template.sections
      .flatMap(section => section.fields)
      .find(f => f.code === name);

    if (!field) return undefined;

    try {
      const schema = createFieldValidationSchema(field);
      schema.parse(formState.data[name]);
      return undefined;
    } catch (error) {
      if (error instanceof z.ZodError) {
        return error.errors[0]?.message || 'Invalid value';
      }
      return 'Validation error';
    }
  }, [template, formState.data]);

  // Validate entire form
  const validateForm = useCallback((): boolean => {
    if (!template) return true;

    const errors: Record<string, string> = {};
    let isValid = true;

    // Validate all fields
    template.sections.forEach((section) => {
      section.fields.forEach((field) => {
        const error = validateField(field.code);
        if (error) {
          errors[field.code] = error;
          isValid = false;
        }
      });
    });

    setFormState((prev) => ({
      ...prev,
      errors,
      isValid,
    }));

    return isValid;
  }, [template, validateField]);

  // Set language
  const setLanguage = useCallback((lang: string) => {
    setCurrentLanguage(lang);
    // TODO: Load translations for the new language
  }, []);

  // Set mode
  const setMode = useCallback((newMode: 'create' | 'edit' | 'view' | 'preview') => {
    setCurrentMode(newMode);
  }, []);

  // Submit form
  const submit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setFormState((prev) => ({ ...prev, isSubmitting: true }));

    try {
      await onSubmit?.(formState.data);
      setFormState((prev) => ({
        ...prev,
        isSubmitting: false,
        isDirty: false,
      }));
    } catch (error) {
      console.error('Form submission error:', error);
      setFormState((prev) => ({
        ...prev,
        isSubmitting: false,
        errors: {
          ...prev.errors,
          _form: error instanceof Error ? error.message : 'Submission failed',
        },
      }));
    }
  }, [formState.data, validateForm, onSubmit]);

  // Reset form
  const reset = useCallback(() => {
    setFormState({
      data: initialData,
      errors: {},
      touched: {},
      isDirty: false,
      isSubmitting: false,
      isValid: true,
    });
  }, [initialData]);

  // Auto-validate on blur if enabled
  useEffect(() => {
    if (template?.validationOnBlur) {
      Object.keys(formState.touched).forEach((field) => {
        if (formState.touched[field]) {
          const error = validateField(field);
          if (error !== formState.errors[field]) {
            setFormState((prev) => ({
              ...prev,
              errors: { ...prev.errors, [field]: error || '' },
            }));
          }
        }
      });
    }
  }, [formState.touched, template, validateField]);

  const contextValue: FormBuilderContextValue = {
    template,
    formState,
    language: currentLanguage,
    mode: currentMode,
    translations,
    updateField,
    touchField,
    validateField,
    validateForm,
    setLanguage,
    setMode,
    submit,
    reset,
    isDirty: formState.isDirty,
    isValid: formState.isValid,
    isSubmitting: formState.isSubmitting,
  };

  return (
    <FormBuilderContext.Provider value={contextValue}>
      {children}
    </FormBuilderContext.Provider>
  );
};

// Hook to use the form builder context
export const useFormBuilderContext = (): FormBuilderContextValue => {
  const context = useContext(FormBuilderContext);
  if (!context) {
    throw new Error('useFormBuilderContext must be used within FormBuilderProvider');
  }
  return context;
};

export default FormBuilderContext;